import { useCallback, useEffect, useRef, useState } from 'react';

export const useSelect = (props) => {
  const {
    defaultValue,
    onChange,
    onSelect = () => null,
    defaultExpaned = false,
  } = props;

  const wrapperRef = useRef();
  const controlRef = useRef();
  const inputRef = useRef(null);
  const focusableRef = useRef(null);
  const fieldRef = useRef(null);
  const optionsMenuRef = useRef(null);
  const optionRefs = useRef(new Map());
  const lastElementSelectionScrollTop = useRef(null);
  const [origIndex, setOrigIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpaned);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);
  const [optionsComputedProps, setOptionsComputedProps] = useState({});

  useEffect(() => {
    setIsExpanded(defaultExpaned.status);
  }, [defaultExpaned]);

  // Select value and trigger select box onChange event
  const handleSelection = (elem, index) => {
    if (props.readOnly) return;
    // update inner <select> and dispatch 'change' event
    elem.selectedIndex = index;
    const event = new Event('change', { bubbles: true });
    elem.dispatchEvent(event);
    setOrigIndex(index);
  };

  // Process Intividual List Item Click event, trigger handle selection method
  const handleItemClick = useCallback(
    (value) => {
      handleSelection(fieldRef.current, value);
      setIsExpanded(false);
    },
    [fieldRef, currentIndex, handleSelection, setIsExpanded],
  );

  // Process Select box click event
  const handleOuterClick = useCallback(
    (ev) => {
      if (
        ev.target.localName === 'li' ||
        ev.button !== 0 ||
        fieldRef.current.disabled
      )
        return;
      focusableRef.current.focus();
      setIsExpanded(!isExpanded);
    },
    [fieldRef, isExpanded, focusableRef],
  );

  // Process Select box keyboard events
  const handleOuterKeyDown = useCallback(
    (ev) => {
      const escape = ev.key === 'Escape';
      const enter = ev.key === 'Enter';
      const arrowUp = ev.key === 'ArrowUp';
      const arrowDown = ev.key === 'ArrowDown';
      const tab = ev.key === 'Tab' && !ev.shiftKey;
      const shiftTab = ev.shiftKey && ev.key === 'Tab';
      const availableOptions = fieldRef.current && fieldRef.current.children;

      if (ev.defaultPrevented) return;

      if (!isExpanded) {
        const { keyCode } = ev;
        // spacebar, down, up
        if (keyCode === 32 || keyCode === 38 || keyCode === 40) {
          // prevent default browser action
          ev.preventDefault();

          // open custom menu
          setIsExpanded(true);
        }
      }

      if (escape && isExpanded) {
        setIsExpanded(false);
      }

      if (enter && isExpanded) {
        // update inner <select> and dispatch 'change' event
        handleSelection(fieldRef.current, currentIndex);
        setIsExpanded(false);
      }

      if (
        (arrowDown || (tab && isExpanded)) &&
        availableOptions.length - 1 === currentIndex
      ) {
        return setCurrentIndex(0);
      }

      if ((arrowDown || (tab && isExpanded)) && currentIndex === null) {
        return setCurrentIndex(0);
      }

      if (arrowDown || (tab && isExpanded)) {
        return setCurrentIndex((prevState) => prevState + 1);
      }
      if ((arrowUp || (shiftTab && isExpanded)) && currentIndex > 0) {
        return setCurrentIndex((prevState) => prevState - 1);
      }

      if ((arrowUp || (shiftTab && isExpanded)) && currentIndex === 0) {
        return setCurrentIndex(availableOptions.length - 1);
      }
    },
    [isExpanded, currentIndex, handleSelection],
  );

  const handleInnerChange = useCallback(
    (ev) => {
      onChange && onChange(ev);
      setSelectedValue(ev.target.value);
      onSelect && onSelect(ev.target.value, ev.target.selectedIndex);
    },
    [onChange],
  );

  const handleInnerMouseDown = useCallback((ev) => {
    if (ev.button !== 0) return;
    ev.preventDefault();
  }, []);

  // Preselect Initial Value
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        return setIsExpanded(false);
      }
    };
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  useEffect(() => {
    if (isExpanded) {
      if (wrapperRef.current) {
        const MAX_OPTIONS = 4;
        const MARGIN_TOP_SIZE = 5;
        const { top: selectTop, height } =
          wrapperRef.current.getBoundingClientRect();
        const top = height;
        const visibleOptions = Math.min(
          fieldRef.current.children.length,
          MAX_OPTIONS,
        );
        const spaceTaken = visibleOptions * 48;
        if (top + spaceTaken + MARGIN_TOP_SIZE > window.innerHeight) {
          const availableSpaceBottom = window.innerHeight - top;
          if (
            top /* this is the available space on top */ > availableSpaceBottom
          ) {
            setOptionsComputedProps({
              bottom: height + 1,
              top: 'auto',
              maxHeight: selectTop - MARGIN_TOP_SIZE,
            });
          } else {
            setOptionsComputedProps({
              top,
              maxHeight: window.innerHeight - top - MARGIN_TOP_SIZE,
            });
          }
        } else {
          // if it fits on bottom
          setOptionsComputedProps({
            top,
            maxHeight: undefined,
          });
        }
      }
    }
  }, [isExpanded]);

  return {
    wrapperRef,
    controlRef,
    inputRef,
    focusableRef,
    optionsMenuRef,
    lastElementSelectionScrollTop,
    fieldRef,
    isExpanded,
    optionRefs,
    handleItemClick,
    selectedValue,
    handleOuterClick,
    handleOuterKeyDown,
    handleInnerMouseDown,
    handleInnerChange,
    currentIndex,
    origIndex,
    isFocused,
    setIsFocused,
    optionsComputedProps,
  };
};
